/**
 * Formatting helpers shared by the report copy and the UI.
 * Times are wall-clock ms (see types.ts): always read with UTC getters.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;
const numberFormat = new Intl.NumberFormat('ko-KR');

export interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  /** 0 = Sunday */
  readonly weekday: number;
}

export function wallClockMs(year: number, month: number, day: number, hour = 0, minute = 0): number {
  return Date.UTC(year, month - 1, day, hour, minute);
}

export function wallClock(ms: number): WallClock {
  const date = new Date(ms);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    weekday: date.getUTCDay(),
  };
}

/** YYYY-MM-DD */
export function dayKey(ms: number): string {
  const { year, month, day } = wallClock(ms);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Wall-clock ms at 00:00 of a YYYY-MM-DD key. */
export function dayKeyToMs(key: string): number {
  const [year, month, day] = key.split('-').map(Number);
  return wallClockMs(year!, month!, day!);
}

/** 2024년 3월 1일 */
export function formatDate(ms: number): string {
  const { year, month, day } = wallClock(ms);
  return `${year}년 ${month}월 ${day}일`;
}

/** 2024년 3월 1일 (금) */
export function formatDateWithWeekday(ms: number): string {
  return `${formatDate(ms)} (${WEEKDAYS[wallClock(ms).weekday]})`;
}

/** 3월 1일 */
export function formatMonthDay(ms: number): string {
  const { month, day } = wallClock(ms);
  return `${month}월 ${day}일`;
}

/** 2024.3.1 */
export function formatCompactDate(ms: number): string {
  const { year, month, day } = wallClock(ms);
  return `${year}.${month}.${day}`;
}

/** 오후 3:07 */
export function formatTime(ms: number): string {
  const { hour, minute } = wallClock(ms);
  const meridiem = hour < 12 ? '오전' : '오후';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${meridiem} ${hour12}:${String(minute).padStart(2, '0')}`;
}

/** 0 -> 자정, 13 -> 오후 1시 */
export function formatHour(hour: number): string {
  if (hour === 0) return '자정';
  if (hour === 12) return '정오';
  return hour < 12 ? `오전 ${hour}시` : `오후 ${hour - 12}시`;
}

export function weekdayName(weekday: number): string {
  return `${WEEKDAYS[((weekday % 7) + 7) % 7]}요일`;
}

/**
 * Minutes -> Korean duration.
 * 0 -> "1분 미만", 45 -> "45분", 160 -> "2시간 40분", 1500 -> "1일 1시간".
 */
export function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return '1분 미만';
  const total = Math.round(minutes);
  if (total < 60) return `${total}분`;
  if (total < 24 * 60) {
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    return rest ? `${hours}시간 ${rest}분` : `${hours}시간`;
  }
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  return hours ? `${days}일 ${hours}시간` : `${days}일`;
}

/** 0.734 -> "73%" ; values below 1% but above 0 -> "1% 미만" */
export function formatPercent(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return '0%';
  if (rate < 0.005) return '1% 미만';
  return `${Math.round(rate * 100)}%`;
}

/** 12345 -> "12,345" */
export function formatCount(value: number): string {
  return numberFormat.format(Math.round(value));
}

/** 9,999 / 1.2만 / 3.4억 */
export function formatCompactCount(value: number): string {
  const n = Math.round(value);
  if (Math.abs(n) < 10_000) return formatCount(n);
  if (Math.abs(n) < 100_000_000) return `${trimDecimal(n / 10_000)}만`;
  return `${trimDecimal(n / 100_000_000)}억`;
}

function trimDecimal(value: number): string {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return numberFormat.format(rounded);
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]!);
}

// ---------------------------------------------------------------------------
// Korean particles (조사)
// ---------------------------------------------------------------------------

export type JosaPair = '은/는' | '이/가' | '을/를' | '과/와' | '이랑/랑' | '아/야' | '으로/로' | '이에요/예요' | '이/';

const DIGIT_BATCHIM: Record<string, 'none' | 'rieul' | 'other'> = {
  0: 'other', 1: 'rieul', 2: 'none', 3: 'other', 4: 'none',
  5: 'none', 6: 'other', 7: 'rieul', 8: 'rieul', 9: 'none',
};

/** 'none' = no final consonant, 'rieul' = ㄹ, 'other' = any other, null = unknown */
export function finalConsonant(word: string): 'none' | 'rieul' | 'other' | null {
  const characters = Array.from(word.trim());
  for (let i = characters.length - 1; i >= 0; i--) {
    const character = characters[i]!;
    const code = character.codePointAt(0)!;
    if (code >= 0xac00 && code <= 0xd7a3) {
      const jong = (code - 0xac00) % 28;
      if (jong === 0) return 'none';
      return jong === 8 ? 'rieul' : 'other';
    }
    if (/[0-9]/.test(character)) return DIGIT_BATCHIM[character] ?? null;
    if (/[A-Za-z]/.test(character)) return null;
    // Skip emoji, punctuation, spaces and other trailing decorations.
  }
  return null;
}

/** Attach a particle: josa('서진', '이/가') -> '서진이'. Unknown endings use '이(가)'. */
export function josa(word: string, pair: JosaPair): string {
  const ending = finalConsonant(word);
  const [withBatchim, withoutBatchim] = pair.split('/') as [string, string];
  if (ending === null) {
    if (pair === '이/') return word;
    return `${word}${withBatchim}(${withoutBatchim})`;
  }
  if (pair === '으로/로') return word + (ending === 'other' ? '으로' : '로');
  return word + (ending === 'none' ? withoutBatchim : withBatchim);
}
